import type {
  ClientCategory,
  ContractHistory,
  LicenceActivityPage,
  LicenceCsm,
  LicenceDetail,
  LicenceNotes,
  ListResponse,
  PrimaryContact,
  UserResponse,
} from '@shared/schemas';
import {
  all,
  call,
  getContext,
  put,
  select,
  spawn,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';

import { pendingCountRequested } from '@/features/list/client-import-alerts';
import { classifyInfraError, isNetworkError, NETWORK_ERROR_MESSAGE } from '@/utils/infra-error';

import {
  CREDITS_GRANT_ERROR,
  CREDITS_LOAD_ERROR,
  toCreditsError,
} from '../domain/credit-error';
import type { CreditHistory } from '../domain/credit-ledger.model';
import type { Approver } from '../domain/licence-detail.model';
import { sortListsByRecency } from '../domain/licence-detail.rules';
import type { LicenceDetailGateway } from '../infrastructure/licence-detail.gateway';
import {
  activityLoadRequested,
  activityMoreRequested,
  approversLoadRequested,
  clientCategoriesLoadRequested,
  clientCategoryAddRequested,
  contractHistoryLoadRequested,
  creditsLoadRequested,
  csmCandidatesLoadRequested,
  editRequested,
  grantCreditsRequested,
  licenceDeleteRequested,
  licenceStatusRequested,
  listApprovalRequested,
  listDuplicateRequested,
  listsDeleteRequested,
  listStatusRequested,
  loadRequested,
  notesLoadRequested,
  notesSaveRequested,
  primaryContactsSaveRequested,
  userArchiveRequested,
  userDeleteRequested,
} from './licence-detail.actions';
import {
  selectActivity,
  selectApprovers,
  selectContractHistory,
  selectLicence,
} from './licence-detail.selectors';
import { licenceDetailActions } from './licence-detail.slice';
import type { ActivityState } from './licence-detail.state';

const DEFAULT_ERROR = 'Failed to send notification.';
const EDIT_ERROR = 'Failed to update licence.';
const USER_DELETE_ERROR = 'Failed to delete the user.';
const USER_ARCHIVE_ERROR = 'Failed to update the user.';
const CONTACTS_ERROR = 'Failed to update the main contacts.';
const LICENCE_STATUS_ERROR = 'Failed to update the licence status.';
const LICENCE_DELETE_ERROR = 'Failed to delete the licence.';
const ACTIVITY_ERROR = 'Failed to load the activity.';
const NOTES_LOAD_ERROR = 'Failed to load the notes.';
const CONTRACT_HISTORY_ERROR = 'Failed to load the contract history.';
const NOTES_SAVE_ERROR = 'Failed to save the notes.';
const LIST_DELETE_ERROR = 'Failed to delete the lists.';
const LIST_STATUS_ERROR = 'Failed to update the list.';
const LIST_APPROVAL_ERROR = 'Failed to ask for the approval.';
const LIST_DUPLICATE_ERROR = 'Failed to duplicate the list.';

/** Human message for a mutation's failure snackbar (no domain error union needed). */
function toMessage(e: unknown, fallback: string = DEFAULT_ERROR): string {
  if (isNetworkError(e)) return NETWORK_ERROR_MESSAGE;
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * WORKER: load the licence + its users + its lists in parallel. `loadStarted`
 * keeps the current `licence` so a re-load never blanks the page. Lists are sorted
 * most-recent first via the pure domain helper before they reach the slice.
 */
function* loadWorker(action: ReturnType<typeof loadRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  const licenceId = action.payload;
  yield put(licenceDetailActions.loadStarted());
  try {
    const [licence, users, lists]: [LicenceDetail, UserResponse[], ListResponse[]] = yield all([
      call(gateway.loadLicence, licenceId),
      call(gateway.loadUsers, licenceId),
      call(gateway.loadLists, licenceId),
    ]);
    yield put(
      licenceDetailActions.loadSucceeded({
        licence,
        users,
        lists: sortListsByRecency(lists),
      }),
    );
  } catch {
    yield put(licenceDetailActions.loadFailed());
  }
}

/** WORKER: delete a user of the licence, then drop the row (no reload needed). */
function* userDeleteWorker(action: ReturnType<typeof userDeleteRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.userDeleteStarted());
  try {
    yield call(gateway.deleteUser, action.payload);
    yield put(licenceDetailActions.userDeleteSucceeded(action.payload));
  } catch (e) {
    yield put(licenceDetailActions.userDeleteFailed(toMessage(e, USER_DELETE_ERROR)));
  }
}

/** WORKER: deactivate or reactivate a user, then patch the row (no reload). */
function* userArchiveWorker(action: ReturnType<typeof userArchiveRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.userArchiveStarted());
  try {
    const { archivedAt }: { archivedAt: string | null } = yield call(
      gateway.setUserArchived,
      action.payload,
    );
    yield put(
      licenceDetailActions.userArchiveSucceeded({ userId: action.payload.userId, archivedAt }),
    );
  } catch (e) {
    yield put(licenceDetailActions.userArchiveFailed(toMessage(e, USER_ARCHIVE_ERROR)));
  }
}

/** WORKER: edit the licence, patching the loaded row from the gateway response. */
function* editWorker(action: ReturnType<typeof editRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.editStarted());
  try {
    const licence: LicenceDetail = yield call(gateway.updateLicence, action.payload);
    yield put(licenceDetailActions.editSucceeded(licence));
    // The contract may have changed (#750): its periods and journal follow.
    yield put(contractHistoryLoadRequested(licence.id));
  } catch (e) {
    yield put(licenceDetailActions.editFailed(toMessage(e, EDIT_ERROR)));
  }
}

/**
 * WORKER: deactivate or reactivate the licence (#582), patching the loaded row
 * from the gateway response so the status chip and the Danger zone card agree.
 */
function* licenceStatusWorker(action: ReturnType<typeof licenceStatusRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.licenceStatusStarted());
  try {
    const result: { licence: LicenceDetail; revokedSessions: number } = yield call(
      gateway.setLicenceStatus,
      action.payload,
    );
    yield put(licenceDetailActions.licenceStatusSucceeded(result));
  } catch (e) {
    yield put(licenceDetailActions.licenceStatusFailed(toMessage(e, LICENCE_STATUS_ERROR)));
  }
}

/** WORKER: delete the licence for good (#584); the page leaves on success. */
function* licenceDeleteWorker(action: ReturnType<typeof licenceDeleteRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.licenceDeleteStarted());
  try {
    yield call(gateway.deleteLicence, action.payload);
    yield put(licenceDetailActions.licenceDeleteSucceeded());
  } catch (e) {
    yield put(licenceDetailActions.licenceDeleteFailed(toMessage(e, LICENCE_DELETE_ERROR)));
  }
}

/** WORKER: save the main contacts; the set the backend returns replaces the loaded one. */
function* contactsSaveWorker(action: ReturnType<typeof primaryContactsSaveRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.contactsSaveStarted());
  try {
    const contacts: PrimaryContact[] = yield call(gateway.setPrimaryContacts, action.payload);
    yield put(licenceDetailActions.contactsSaveSucceeded(contacts));
  } catch (e) {
    yield put(licenceDetailActions.contactsSaveFailed(toMessage(e, CONTACTS_ERROR)));
  }
}

/** WORKER: load the credit balance + ledger for the Credits tab. */
function* creditsLoadWorker(action: ReturnType<typeof creditsLoadRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.creditsLoadStarted());
  try {
    const history: CreditHistory = yield call(gateway.loadCreditHistory, action.payload);
    yield put(licenceDetailActions.creditsLoadSucceeded(history));
  } catch (e) {
    yield put(licenceDetailActions.creditsLoadFailed(toCreditsError(e, CREDITS_LOAD_ERROR)));
  }
}

/**
 * WORKER: grant credits, then re-request the history so the balance, chart and
 * table reflect the new movement. The explicit refetch replaces the implicit
 * `queryClient.invalidateQueries` of the React Query hook this replaced.
 */
function* grantCreditsWorker(action: ReturnType<typeof grantCreditsRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.grantStarted());
  try {
    yield call(gateway.grantCredits, action.payload);
    yield put(licenceDetailActions.grantSucceeded());
    yield put(creditsLoadRequested(action.payload.licenceId));
  } catch (e) {
    yield put(licenceDetailActions.grantFailed(toCreditsError(e, CREDITS_GRANT_ERROR)));
  }
}

/** WORKER: load the newest page of the licence's activity feed (#566). */
function* activityLoadWorker(action: ReturnType<typeof activityLoadRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  const licenceId = action.payload;
  yield put(licenceDetailActions.activityLoadStarted(licenceId));
  try {
    const page: LicenceActivityPage = yield call(gateway.loadActivity, { licenceId });
    yield put(licenceDetailActions.activityLoadSucceeded({ licenceId, page }));
  } catch (e) {
    yield put(
      licenceDetailActions.activityLoadFailed({ licenceId, message: toMessage(e, ACTIVITY_ERROR) }),
    );
  }
}

/** WORKER: load the licence's internal notes for the Notes tab (#605). */
/** WORKER: load the contract periods and journal (#750). */
function* contractHistoryLoadWorker(action: ReturnType<typeof contractHistoryLoadRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  const licenceId = action.payload;
  yield put(licenceDetailActions.contractHistoryLoadStarted(licenceId));
  try {
    const history: ContractHistory = yield call(gateway.loadContractHistory, licenceId);
    yield put(licenceDetailActions.contractHistoryLoadSucceeded({ licenceId, history }));
  } catch (e) {
    yield put(
      licenceDetailActions.contractHistoryLoadFailed({
        licenceId,
        message: toMessage(e, CONTRACT_HISTORY_ERROR),
      }),
    );
  }
}

function* notesLoadWorker(action: ReturnType<typeof notesLoadRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  const licenceId = action.payload;
  yield put(licenceDetailActions.notesLoadStarted(licenceId));
  try {
    const notes: LicenceNotes = yield call(gateway.loadNotes, licenceId);
    yield put(licenceDetailActions.notesLoadSucceeded({ licenceId, notes }));
  } catch (e) {
    yield put(
      licenceDetailActions.notesLoadFailed({ licenceId, message: toMessage(e, NOTES_LOAD_ERROR) }),
    );
  }
}

/** WORKER: save the licence's internal notes; the server's copy replaces the shown one (#605). */
function* notesSaveWorker(action: ReturnType<typeof notesSaveRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  const { licenceId } = action.payload;
  yield put(licenceDetailActions.notesSaveStarted());
  try {
    const notes: LicenceNotes = yield call(gateway.saveNotes, action.payload);
    yield put(licenceDetailActions.notesSaveSucceeded({ licenceId, notes }));
  } catch (e) {
    yield put(licenceDetailActions.notesSaveFailed(toMessage(e, NOTES_SAVE_ERROR)));
  }
}

/**
 * WORKER: load the next, older page. The store says which licence and where the
 * page starts; nothing happens until the first page is in, once the last one is,
 * or while a first-page reload is running.
 */
function* activityMoreWorker() {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  const { licenceId, nextCursor, status }: ActivityState = yield select(selectActivity);
  if (!licenceId || !nextCursor || status !== 'loaded') return;

  yield put(licenceDetailActions.activityMoreStarted());
  try {
    const page: LicenceActivityPage = yield call(gateway.loadActivity, {
      licenceId,
      cursor: nextCursor,
    });
    yield put(licenceDetailActions.activityMoreSucceeded({ licenceId, page }));
  } catch (e) {
    yield put(
      licenceDetailActions.activityMoreFailed({ licenceId, message: toMessage(e, ACTIVITY_ERROR) }),
    );
  }
}

/** WORKER: delete one or several lists, then drop the rows (#577). */
function* listsDeleteWorker(action: ReturnType<typeof listsDeleteRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.listMutationStarted('delete'));
  try {
    yield call(gateway.deleteLists, action.payload);
    yield put(licenceDetailActions.listsDeleteSucceeded(action.payload));
    // The Lists tab shows client imports too: they leave the Collection badge (#748).
    yield put(pendingCountRequested());
  } catch (e) {
    yield put(licenceDetailActions.listMutationFailed(toMessage(e, LIST_DELETE_ERROR)));
  }
}

/** WORKER: move a list to a status (Update status, Send to client), then swap the row. */
function* listStatusWorker(action: ReturnType<typeof listStatusRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.listMutationStarted('status'));
  try {
    const list: ListResponse = yield call(gateway.updateListStatus, action.payload);
    yield put(licenceDetailActions.listUpdateSucceeded(list));
    // A client import moved off draft leaves the Collection badge (#748).
    yield put(pendingCountRequested());
  } catch (e) {
    yield put(licenceDetailActions.listMutationFailed(toMessage(e, LIST_STATUS_ERROR)));
    return;
  }
  // A list reaching `sent` delivers its profiles (#750): Profiles Delivered in
  // At a Glance and the Contract card must follow without a page reload.
  // Detached (`spawn`): the `takeLeading` watcher is free again as soon as the
  // status is saved, so the admin's next status change is never dropped.
  yield spawn(refreshContractFigures);
}

/**
 * Re-read the licence (its `profiles_delivered`) and, when it was loaded, the
 * contract history. Best effort: the list mutation already succeeded, so a
 * failed re-read keeps the figures on screen rather than reporting an error.
 */
function* refreshContractFigures() {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  const licence: LicenceDetail | null = yield select(selectLicence);
  if (!licence) return;
  try {
    const fresh: LicenceDetail = yield call(gateway.loadLicence, licence.id);
    yield put(licenceDetailActions.licenceRefreshed(fresh));
  } catch {
    return;
  }
  const history: ReturnType<typeof selectContractHistory> = yield select(selectContractHistory);
  if (history.licenceId === licence.id) yield put(contractHistoryLoadRequested(licence.id));
}

/** WORKER: ask an administrator to approve a list, then swap the row (now pending review). */
function* listApprovalWorker(action: ReturnType<typeof listApprovalRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.listMutationStarted('approval'));
  try {
    const list: ListResponse = yield call(gateway.requestListApproval, action.payload);
    yield put(licenceDetailActions.listUpdateSucceeded(list));
    // Now pending review, so off draft: it leaves the Collection badge (#748).
    yield put(pendingCountRequested());
  } catch (e) {
    yield put(licenceDetailActions.listMutationFailed(toMessage(e, LIST_APPROVAL_ERROR)));
  }
}

/** WORKER: copy a list into the licence, then add the copy to the rows (#662). */
function* listDuplicateWorker(action: ReturnType<typeof listDuplicateRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.listMutationStarted('duplicate'));
  try {
    const copy: ListResponse = yield call(gateway.duplicateList, {
      listId: action.payload.listId,
      name: action.payload.name.trim(),
    });
    yield put(licenceDetailActions.listDuplicateSucceeded(copy));
  } catch (e) {
    yield put(licenceDetailActions.listMutationFailed(toMessage(e, LIST_DUPLICATE_ERROR)));
  }
}

/** WORKER: load the approvers once; a second opening of the dialog reuses them. */
function* approversLoadWorker() {
  const { status }: { status: string } = yield select(selectApprovers);
  if (status === 'loaded' || status === 'loading') return;
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.approversLoadStarted());
  try {
    const approvers: Approver[] = yield call(gateway.loadApprovers);
    yield put(licenceDetailActions.approversLoadSucceeded(approvers));
  } catch {
    yield put(licenceDetailActions.approversLoadFailed());
  }
}

/**
 * WORKER: load who may be picked as the CSM (#583), each time Edit licence opens:
 * a role change made since on the Administrators page shows up. A failure leaves
 * the select with "None" and the CSM on record; the field is optional.
 */
function* csmCandidatesLoadWorker() {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.csmCandidatesLoadStarted());
  try {
    const candidates: LicenceCsm[] = yield call(gateway.loadCsmCandidates);
    yield put(licenceDetailActions.csmCandidatesLoadSucceeded(candidates));
  } catch {
    yield put(licenceDetailActions.csmCandidatesLoadFailed());
  }
}

/**
 * WORKER: load the client category list (#604), each time Edit licence opens. A
 * failure leaves the licence's own category in the select: it can still be saved.
 */
function* clientCategoriesLoadWorker() {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.clientCategoriesLoadStarted());
  try {
    const categories: ClientCategory[] = yield call(gateway.loadClientCategories);
    yield put(licenceDetailActions.clientCategoriesLoadSucceeded(categories));
  } catch {
    yield put(licenceDetailActions.clientCategoriesLoadFailed());
  }
}

/**
 * WORKER: add a client category from the Edit licence select (#604). The form
 * picks it on success; a refusal (a duplicate, an empty or too long name) is
 * shown under the input as the server words it.
 */
function* clientCategoryAddWorker(action: ReturnType<typeof clientCategoryAddRequested>) {
  const gateway: LicenceDetailGateway = yield getContext('licenceDetailGateway');
  yield put(licenceDetailActions.clientCategoryAddStarted());
  try {
    const category: ClientCategory = yield call(gateway.addClientCategory, action.payload);
    yield put(licenceDetailActions.clientCategoryAddSucceeded(category));
  } catch (e) {
    yield put(
      licenceDetailActions.clientCategoryAddFailed(
        classifyInfraError(e, "The category couldn't be added.").message,
      ),
    );
  }
}

/** WATCHERS. */
export function* licenceDetailSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    // `takeEvery`: two deletions are two different rows, neither supersedes the other.
    takeEvery(userDeleteRequested.type, userDeleteWorker),
    takeEvery(userArchiveRequested.type, userArchiveWorker),
    takeLatest(editRequested.type, editWorker),
    // `takeLatest`: the set sent last is the one the admin wants; a superseded
    // save is cancelled rather than landing after it.
    takeLatest(primaryContactsSaveRequested.type, contactsSaveWorker),
    // `takeLeading`: one list action at a time; its dialog waits for it, and a
    // double click must not send the same deletion or request twice.
    takeLeading(listsDeleteRequested.type, listsDeleteWorker),
    takeLeading(listStatusRequested.type, listStatusWorker),
    takeLeading(listApprovalRequested.type, listApprovalWorker),
    takeLeading(listDuplicateRequested.type, listDuplicateWorker),
    takeLeading(approversLoadRequested.type, approversLoadWorker),
    takeLatest(csmCandidatesLoadRequested.type, csmCandidatesLoadWorker),
    takeLatest(clientCategoriesLoadRequested.type, clientCategoriesLoadWorker),
    // `takeLeading`: a double Enter must not send the same category twice.
    takeLeading(clientCategoryAddRequested.type, clientCategoryAddWorker),
    takeLatest(creditsLoadRequested.type, creditsLoadWorker),
    takeLatest(licenceStatusRequested.type, licenceStatusWorker),
    // `takeLeading`: a second click while the first deletion is in flight is ignored.
    takeLeading(licenceDeleteRequested.type, licenceDeleteWorker),
    takeLatest(grantCreditsRequested.type, grantCreditsWorker),
    takeLatest(activityLoadRequested.type, activityLoadWorker),
    takeLatest(notesLoadRequested.type, notesLoadWorker),
    takeLatest(contractHistoryLoadRequested.type, contractHistoryLoadWorker),
    // `takeLeading`: a double click on Save must not send the same notes twice.
    takeLeading(notesSaveRequested.type, notesSaveWorker),
    // `takeLeading`: the card may signal "near the bottom" several times while one
    // page is on its way; those are the same request, not new ones, and letting
    // them through would fetch the same cursor twice and append its events twice.
    takeLeading(activityMoreRequested.type, activityMoreWorker),
  ]);
}
